import { useEffect, useRef } from "react";

/**
 * Adds .is-visible to elements with .reveal once they intersect the viewport.
 * Re-observes on `lang` change (the `<html dir>` attribute flips on EN/AR toggle),
 * which is cheaper than a body-subtree MutationObserver.
 */
export default function useReveal() {
  const ioRef = useRef(null);

  useEffect(() => {
    const reveal = (el) => {
      if (!el.classList.contains("is-visible")) el.classList.add("is-visible");
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.01, rootMargin: "0px 0px -10% 0px" }
    );
    ioRef.current = io;

    const observe = () => {
      const els = document.querySelectorAll(".reveal:not(.is-visible)");
      const vh = window.innerHeight || 800;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.95) {
          reveal(el);
        } else {
          io.observe(el);
        }
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(observe));

    // Re-observe when language flips (RTL/LTR swap rerenders the section trees).
    const onDirChange = () => requestAnimationFrame(observe);
    const dirObserver = new MutationObserver(onDirChange);
    dirObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir", "lang"],
    });

    return () => {
      io.disconnect();
      dirObserver.disconnect();
    };
  }, []);
}
